import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../contexts/AuthContext';
import {
  GlobeIcon,
  SunIcon,
  MoonIcon,
  MonitorIcon,
  CheckIcon,
  UserIcon,
  ArrowRightIcon
} from './Icons';

export default function TopNav() {
  const { theme, setThemeMode } = useTheme();
  const { loading, isAuthenticated, getActiveUser } = useAuth();
  const user = getActiveUser();
  const [open, setOpen] = useState(false);
  const themeButtonRef = useRef(null);
  const themeDropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (open) {
        const btn = themeButtonRef.current;
        const menu = themeDropdownRef.current;
        if ((!btn || !btn.contains(e.target)) && (!menu || !menu.contains(e.target))) {
          setOpen(false);
        }
      }
    }

    function handleEsc(e) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const openAuthPopup = () => {
    window.open('/auth/popup', 'AuthPopup', 'width=500,height=650');
  };

  return (
    <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-6 py-3.5">
      <div className="w-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group cursor-pointer">
          <div className="p-2 rounded-xl bg-indigo-600/10 text-indigo-600 dark:text-indigo-400 group-hover:scale-105 transition-transform">
            <GlobeIcon className="w-7 h-7" />
          </div>
          <span className="text-base font-bold tracking-tight text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
            OneLogin SSO
          </span>
        </Link>

        {/* Top Links & Theme */}
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-6 text-xs font-semibold text-slate-600 dark:text-slate-400">
            <Link to="/about" className="hover:text-indigo-600 dark:hover:text-white transition-colors">About</Link>
            <Link to="/help" className="hover:text-indigo-600 dark:hover:text-white transition-colors">Help</Link>
            <Link to="/contact" className="hover:text-indigo-600 dark:hover:text-white transition-colors">Contact</Link>
          </div>

          {/* Theme Selector */}
          <div className="relative">
            <button
              ref={themeButtonRef}
              onClick={() => setOpen(!open)}
              className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Theme selector"
            >
              {theme === 'light' ? <SunIcon className="w-5 h-5 text-amber-500" /> : theme === 'dark' ? <MoonIcon className="w-5 h-5 text-indigo-400" /> : <MonitorIcon className="w-5 h-5 text-slate-400" />}
            </button>

            {open && (
              <div
                ref={themeDropdownRef}
                className="absolute right-0 mt-2 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden"
              >
                <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                  <p className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Appearance</p>
                </div>
                <div className="p-1 space-y-0.5">
                  {[
                    { value: 'light', icon: <SunIcon className="w-4 h-4 text-amber-500" />, label: 'Light' },
                    { value: 'dark', icon: <MoonIcon className="w-4 h-4 text-indigo-400" />, label: 'Dark' },
                    { value: 'system', icon: <MonitorIcon className="w-4 h-4 text-slate-400" />, label: 'System' },
                  ].map((o) => (
                    <button
                      key={o.value}
                      onClick={() => {
                        setThemeMode(o.value);
                        setOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between text-xs transition-colors ${
                        theme === o.value
                          ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 font-semibold'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        {o.icon}
                        <span>{o.label}</span>
                      </div>
                      {theme === o.value && <CheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* User / Sign In CTA */}
          {loading && !user ? (
            <div className="w-24 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
          ) : !isAuthenticated ? (
            <button
              onClick={openAuthPopup}
              className="px-4 py-2 bg-indigo-600 text-white font-semibold text-xs rounded-xl shadow-md shadow-indigo-600/20 hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
            >
              <span>Sign in</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          ) : (
            <Link
              to="/u/0/dashboard/overview"
              className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <UserIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>{user?.first_name || user?.username || 'Dashboard'}</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
